import React, { memo, useContext } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import homePositionButton from 'assets/images/homePositionButton.svg';
import customHomePositionButton from 'assets/images/customHomePositionButton.svg';
import endPositionButton from 'assets/images/endPositionButton.svg';
import defaultPositionButton from 'assets/images/defaultPositionButton.svg';
import { TextBox } from 'components/system/atoms';
import { PositionControllerGrandType } from 'types/machineConf/controllerType';
import COMMON from 'constants/common';
import MachineConfContext from 'Contexts/MachineConf/MachineConfContext';

/**
 * PositionController Component
 * ポジションコントローラー
 * @author atsushi.teruya
 *
 * @param props
 * @returns {*}
 * @constructor
 */

type StyleProps = {
  leftRight?: string;
};

// makeStylesでstyleを定義
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  xRoot: {
    display: 'flex',
    alignItems: 'center',
    height: '52px',
    '& .inputDirection': {
      color: theme.palette.common.black,
      margin: '0px 4px',
    },
    '& .buttonPre': {},
    '& .buttonPost': {
      transform: 'rotateY(180deg)',
    },
  },
  yRoot: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '52px',
    '& .inputDirection': {
      color: theme.palette.common.black,
      margin: '4px 0px',
    },
    '& .buttonPre': {
      transform: 'rotate(90deg)',
    },
    '& .buttonPost': {
      transform: 'rotate(-90deg)',
      '& span': {
        transform: 'rotate(90deg)',
      },
    },
  },
  buttonRoot: {
    paddingLeft: '24px',
    minWidth: '48px',
  },
  buttonLabel: {
    maxWidth: '48px',
    color: '#FFFFFF',
  },
  buttonContained: {
    color: theme.palette.common.black,
    height: '48px',
    width: '48px',
    fontSize: '24px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0)',
      boxShadow: 'none',
    },
    '&:active': {
      boxShadow: 'none',
    },
  },
  homeButtonContained: {
    background: `url(${homePositionButton})`,
    '&:active': {
      background: `url(${homePositionButton})`,
    },
  },
  endButtonContained: {
    background: `url(${endPositionButton})`,
    '&:active': {
      background: `url(${endPositionButton})`,
    },
  },
  customHomeButtonContained: {
    background: `url(${customHomePositionButton})`,
    '&:active': {
      background: `url(${customHomePositionButton})`,
    },
  },
  defaultButtonContained: {
    background: `url(${defaultPositionButton})`,
    '&:active': {
      background: `url(${defaultPositionButton})`,
    },
  },
  textBox: {
    backgroundColor: theme.palette.common.white,
    height: '52px',
    width: '52px',
    '&.inputDirection': {
      color: theme.palette.common.black,
    },
  },
  input: {
    textAlign: 'center',
    fontSize: '28px',
  },
  HStyle: {
    borderColor: '#FF3E3E',
  },
  EStyle: {
    borderColor: '#3086FF',
  },
  HPStyle: {
    borderColor: '#FFB54E',
  },
  DefaultStyle: {
    borderColor:
      props.leftRight === COMMON.LEFT_RIGHT.LEFT ? theme.leftStation.itemBorder : theme.rightStation.itemBorder,
  },
}));

// コンポーネント定義
const PositionControllerGrand: React.FC<React.PropsWithChildren<PositionControllerGrandType>> = ({
  type,
  axis,
  value,
}) => {
  // makeStyleで定義したstyleを取得
  const { leftRight } = useContext(MachineConfContext); //
  const { classes } = useStyles({ leftRight });

  // タイプに応じた配色を定義
  let typeStyle = '';
  // デフォルトカラーじゃない場合は左右のスタイルを設定
  switch (type) {
    case COMMON.POINTER_TYPE.HOME:
      typeStyle = classes.HStyle;
      break;
    case COMMON.POINTER_TYPE.END:
      typeStyle = classes.EStyle;
      break;
    case COMMON.POINTER_TYPE.CUSTOM_HOME:
      typeStyle = classes.HPStyle;
      break;
    default:
      typeStyle = classes.DefaultStyle;
      break;
  }

  return (
    <Box className={axis === 'X' ? classes.xRoot : classes.yRoot}>
      <TextBox
        classes={{
          root: `inputDirection`,
          input: classes.input,
        }}
        className={`${classes.textBox} ${typeStyle}`}
        disabled
        value={value}
      />
    </Box>
  );
};

// レンダーが前回と変わらない場合前回のレンダーを再利用
export default memo(PositionControllerGrand);
